/**
 * InvoicesList Component
 * Display list of invoices with pagination
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import { AlertCircle, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InvoiceCard } from './InvoiceCard';
import type { InvoicesResponse } from '../types/invoice.types';

// ============================================
// SKELETON LOADER
// ============================================

const InvoiceSkeleton = () => (
  <Card className="p-4 mb-4 border-slate-200 animate-pulse">
    <div className="space-y-3">
      <div className="h-6 bg-slate-200 rounded w-1/3" />
      <div className="h-4 bg-slate-200 rounded w-1/2" />
      <div className="flex justify-between">
        <div className="h-4 bg-slate-200 rounded w-1/4" />
        <div className="h-4 bg-slate-200 rounded w-1/4" />
      </div>
    </div>
  </Card>
);

// ============================================
// COMPONENT PROPS
// ============================================

export interface InvoicesListProps {
  data?: InvoicesResponse;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onViewDetails: (invoiceId: number) => void;
  onPageChange: (page: number) => void;
  currentPage: number;
}

// ============================================
// COMPONENT
// ============================================

/**
 * InvoicesList Component
 * Renders list of invoices with loading, error, and empty states
 *
 * @example
 * ```tsx
 * <InvoicesList
 *   data={invoices}
 *   isLoading={loading}
 *   isError={error}
 *   error={errorObject}
 *   onViewDetails={handleViewDetails}
 *   onPageChange={handlePageChange}
 *   currentPage={0}
 * />
 * ```
 */
export function InvoicesList({
  data,
  isLoading,
  isError,
  error,
  onViewDetails,
  onPageChange,
  currentPage,
}: InvoicesListProps) {
  // Loading State
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <InvoiceSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Error State
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error?.message || 'Failed to load invoices. Please try again.'}
        </AlertDescription>
      </Alert>
    );
  }

  // Empty State
  if (!data?.invoices || data.invoices.length === 0) {
    return (
      <Card className="p-12 text-center border-slate-200 bg-white">
        <Package className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          No Invoices Found
        </h3>
        <p className="text-slate-600">
          Try adjusting your filters or search criteria
        </p>
      </Card>
    );
  }

  const totalPages = data?.totalPages || 1;
  const hasNext = data?.hasNext || false;
  const hasPrevious = data?.hasPrevious || false;

  return (
    <div className="space-y-6">
      {/* Invoice Cards */}
      <div className="space-y-4">
        {data.invoices.map((invoice) => (
          <InvoiceCard
            key={invoice.invoiceId}
            invoice={invoice}
            onViewDetails={onViewDetails}
          />
        ))}
      </div>

      {/* Results Summary */}
      <div className="text-sm text-slate-600">
        Showing {data.invoices.length} of {data.totalElements} invoices
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPrevious}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="px-3 py-2 text-sm text-slate-600 font-medium">
            Page {currentPage + 1} of {totalPages}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNext}
            className="gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default InvoicesList;
